import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { SchemaFieldProps } from '../types';

/**
 * Emergency contact jsonb shape persisted by the backend (`members.emergencyContact`).
 */
export interface EmergencyContactValue {
  name?: string;
  phone?: string;
  relationship?: string;
}

/**
 * Custom SchemaForm widget for the member `emergencyContact` jsonb field. value
 * is the emergency contact object (or null); onChange emits the merged object.
 *
 * Mirrors AddressField — the established convention for jsonb override fields.
 */
const EmergencyContactField: React.FC<SchemaFieldProps> = ({ field, value, onChange, error }) => {
  const { t } = useTranslation();
  const ec: EmergencyContactValue = value ?? {};

  const setPart = useCallback(
    (part: keyof EmergencyContactValue, partValue: string) => {
      onChange({ ...ec, [part]: partValue });
    },
    [ec, onChange]
  );

  return (
    <div className="form-section schema-form__section">
      <h4 className="form-section__title">
        {t(field.labelKey, field.name)}
        {field.required && (
          <span style={{ color: 'var(--danger, #dc3545)', marginLeft: '0.25rem' }}>*</span>
        )}
      </h4>
      {error && (
        <span
          className="form-error"
          style={{ color: 'var(--danger, #dc3545)', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}
        >
          {error}
        </span>
      )}
      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="ec-name">{t('members.ecName')}</label>
          <input
            type="text"
            id="ec-name"
            placeholder={t('members.enterEcName')}
            value={ec.name ?? ''}
            onChange={(e) => setPart('name', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="ec-phone">{t('members.ecPhone')}</label>
          <input
            type="text"
            id="ec-phone"
            placeholder={t('members.enterEcPhone')}
            value={ec.phone ?? ''}
            onChange={(e) => setPart('phone', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="ec-relationship">{t('members.ecRelationship')}</label>
          <input
            type="text"
            id="ec-relationship"
            placeholder={t('members.enterEcRelationship')}
            value={ec.relationship ?? ''}
            onChange={(e) => setPart('relationship', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default EmergencyContactField;
